/* UI chrome i18n — nav labels, buttons, form labels, alerts. Bundled at
 * build time from tools/i18n.py (see frontend/src/i18n/strings.*.json,
 * generated once by a small export script — the same single source of
 * truth as before, just delivered as a static bundle instead of
 * server-rendered HTML). This is distinct from CMS content (Solutions,
 * Industries, Pages, ...), which always comes from the backend API —
 * see api/client.ts — never from a bundled file. */
import React, { createContext, useContext, useMemo } from 'react';
import en from './strings.en.json';
import fr from './strings.fr.json';
import ar from './strings.ar.json';

export type Locale = 'en' | 'fr' | 'ar';
export const LOCALES: Locale[] = ['en', 'fr', 'ar'];
export const LOCALE_DIR: Record<Locale, 'ltr' | 'rtl'> = { en: 'ltr', fr: 'ltr', ar: 'rtl' };
export const LOCALE_LABEL: Record<Locale, string> = { en: 'EN', fr: 'FR', ar: 'AR' };

const BUNDLES: Record<Locale, Record<string, string>> = { en, fr, ar };

const LocaleContext = createContext<Locale>('en');

export function LocaleProvider({ locale, children }: { locale: Locale; children: React.ReactNode }) {
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

export function useT() {
  const locale = useLocale();
  return useMemo(() => {
    const bundle = BUNDLES[locale] ?? BUNDLES.en;
    return (key: string, vars?: Record<string, string | number>) => {
      let str = bundle[key] ?? BUNDLES.en[key] ?? key;
      if (vars) {
        for (const [k, v] of Object.entries(vars)) str = str.replace(`{${k}}`, String(v));
      }
      return str;
    };
  }, [locale]);
}

/** Prefix an internal path with the current locale, mirroring the old
 * static site's u(path, lang) helper — /solutions.html -> /en/solutions. */
export function withLocale(path: string, locale: Locale): string {
  if (path === '/') return `/${locale}`;
  return `/${locale}${path}`;
}
